import React, {useState, useEffect, useCallback} from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {firestore} from "../../services/firebase/config";
import {
  FeedbackData,
  deleteScreenshot,
} from "../../services/api/feedbackService";
import {
  MessageSquare,
  EyeOff,
  Trash2,
  CheckCircle,
  User,
  Bug,
  Download,
} from "lucide-react";
import {formatLoggedAtDate, formatJoinDate} from "../../utils/dateUtils";
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog";
import {
  convertFeedbackToCSV,
  downloadCSV,
  generateCSVFilename,
} from "../../utils/csvUtils";

interface FeedbackWithId extends FeedbackData {
  id: string;
  timestamp: any;
  userAgent?: string;
}

const FeedbackManagement: React.FC = () => {
  const [feedback, setFeedback] = useState<FeedbackWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] =
    useState<FeedbackWithId | null>(null);
  const [filter, setFilter] = useState<"all" | "general" | "anonymous" | "bug">(
    "all"
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteFeedbackId, setPendingDeleteFeedbackId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const feedbackQuery = query(
      collection(firestore, "feedback"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(feedbackQuery, (snapshot) => {
      const feedbackData: FeedbackWithId[] = [];
      snapshot.forEach((doc) => {
        feedbackData.push({
          id: doc.id,
          ...doc.data(),
        } as FeedbackWithId);
      });
      setFeedback(feedbackData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Using centralized date utilities
  const formatDate = formatLoggedAtDate;
  const formatDateShort = formatJoinDate;

  const filteredFeedback = feedback.filter((item) => {
    if (filter === "all") return true;
    return item.type === filter;
  });

  const unreadCount = feedback.filter((item) => !item.read).length;

  const handleMarkAsRead = useCallback(async (feedbackId: string) => {
    try {
      await updateDoc(doc(firestore, "feedback", feedbackId), {
        read: true,
        readAt: new Date(),
      });
    } catch (error) {
      // Error marking feedback as read - silent failure
    }
  }, []);

  const handleDeleteFeedback = useCallback((feedbackId: string) => {
    setPendingDeleteFeedbackId(feedbackId);
    setDeleteDialogOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setPendingDeleteFeedbackId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteFeedbackId) {
      try {
        // Find the feedback item to get the screenshot URL
        const feedbackToDelete = feedback.find(
          (f) => f.id === pendingDeleteFeedbackId
        );

        // Delete the feedback document
        await deleteDoc(doc(firestore, "feedback", pendingDeleteFeedbackId));

        // If there's a screenshot, delete it from storage
        if (feedbackToDelete?.screenshotUrl) {
          try {
            await deleteScreenshot(feedbackToDelete.screenshotUrl);
          } catch (screenshotError) {
            // Error deleting screenshot - log but don't fail the entire operation
            console.error("Failed to delete screenshot:", screenshotError);
          }
        }

        if (selectedFeedback?.id === pendingDeleteFeedbackId) {
          setSelectedFeedback(null);
        }
      } catch (error) {
        // Error deleting feedback - silent failure
        console.error("Failed to delete feedback:", error);
      }
    }
    setDeleteDialogOpen(false);
    setPendingDeleteFeedbackId(null);
  }, [pendingDeleteFeedbackId, selectedFeedback, feedback]);

  const handleDownloadCSV = useCallback(() => {
    try {
      // Use filtered feedback for download (respects current filter)
      const csvContent = convertFeedbackToCSV(filteredFeedback);
      const filename = generateCSVFilename("feedback");
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error("Failed to download CSV:", error);
      // Could add a toast notification here for user feedback
    }
  }, [filteredFeedback]);

  if (loading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Feedback Management</h2>
        <section className="env-section">
          <div className="env-section-header">FEEDBACK LIST</div>
          <div className="env-loading-state">
            <div className="loading-spinner"></div>
            <p>Loading feedback...</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Feedback Management</h2>

      {/* Stats Section */}
      <section className="env-section">
        <div className="env-section-header">OVERVIEW</div>
        <div className="env-overview-stats">
          <div className="env-overview-stat">
            <div className="env-overview-stat-label">Total Feedback</div>
            <div className="env-overview-stat-value">{feedback.length}</div>
          </div>
          {unreadCount > 0 && (
            <div className="env-overview-stat">
              <div className="env-overview-stat-label">Unread</div>
              <div className="env-overview-stat-value unread">
                {unreadCount}
              </div>
            </div>
          )}
          <div className="env-overview-stat">
            <div className="env-overview-stat-label">General</div>
            <div className="env-overview-stat-value">
              {feedback.filter((f) => f.type === "general").length}
            </div>
          </div>
          <div className="env-overview-stat">
            <div className="env-overview-stat-label">Anonymous</div>
            <div className="env-overview-stat-value">
              {feedback.filter((f) => f.type === "anonymous").length}
            </div>
          </div>
          <div className="env-overview-stat">
            <div className="env-overview-stat-label">Bug Reports</div>
            <div className="env-overview-stat-value">
              {feedback.filter((f) => f.type === "bug").length}
            </div>
          </div>
        </div>

        {/* Download CSV Button */}
        <div className="env-section-actions">
          <button
            className="btn btn-primary"
            onClick={handleDownloadCSV}
            disabled={filteredFeedback.length === 0}
            title={
              filteredFeedback.length === 0
                ? "No feedback to download"
                : "Download filtered feedback as CSV"
            }
          >
            <Download size={16} />
            Download CSV ({filteredFeedback.length} items)
          </button>
        </div>
      </section>

      {/* Filters Section */}
      <section className="env-section">
        <div className="env-section-header">FILTERS</div>
        <div className="env-filters-grid">
          <div className="env-filter-group">
            <div className="env-filter-label">Type</div>
            <div className="env-checkbox-group">
              <label className="env-checkbox-label">
                <input
                  type="radio"
                  name="filter"
                  checked={filter === "all"}
                  onChange={() => setFilter("all")}
                  className="env-checkbox"
                />
                All ({feedback.length})
              </label>
              <label className="env-checkbox-label">
                <input
                  type="radio"
                  name="filter"
                  checked={filter === "general"}
                  onChange={() => setFilter("general")}
                  className="env-checkbox"
                />
                General ({feedback.filter((f) => f.type === "general").length})
              </label>
              <label className="env-checkbox-label">
                <input
                  type="radio"
                  name="filter"
                  checked={filter === "anonymous"}
                  onChange={() => setFilter("anonymous")}
                  className="env-checkbox"
                />
                Anonymous (
                {feedback.filter((f) => f.type === "anonymous").length})
              </label>
              <label className="env-checkbox-label">
                <input
                  type="radio"
                  name="filter"
                  checked={filter === "bug"}
                  onChange={() => setFilter("bug")}
                  className="env-checkbox"
                />
                Bug Reports ({feedback.filter((f) => f.type === "bug").length})
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback List Section */}
      <section className="env-section">
        <div className="env-section-header">FEEDBACK LIST</div>
        {filteredFeedback.length === 0 ? (
          <div className="env-empty-state">
            <MessageSquare size={48} />
            <h3>No feedback found</h3>
            <p>
              There are no feedback submissions matching your current filters.
            </p>
          </div>
        ) : (
          <div className="env-feedback-table-wrapper">
            <table className="env-feedback-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Subject</th>
                  <th>From</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeedback.map((item) => (
                  <tr
                    key={item.id}
                    className={`env-feedback-row ${!item.read ? "unread" : ""} ${
                      selectedFeedback?.id === item.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedFeedback(item)}
                  >
                    <td>
                      <div className="env-feedback-type">
                        {item.type === "anonymous" ? (
                          <EyeOff size={16} />
                        ) : item.type === "bug" ? (
                          <Bug size={16} />
                        ) : (
                          <User size={16} />
                        )}
                        <span>
                          {item.type === "anonymous"
                            ? "Anonymous"
                            : item.type === "bug"
                              ? "Bug Report"
                              : "General"}
                        </span>
                      </div>
                    </td>
                    <td className="env-feedback-subject">{item.subject}</td>
                    <td className="env-feedback-author">
                      {item.type === "general" && item.name
                        ? item.name
                        : "Anonymous"}
                    </td>
                    <td className="env-feedback-date">
                      {formatDateShort(item.timestamp)}
                    </td>
                    <td>
                      {!item.read ? (
                        <span className="env-feedback-status unread">New</span>
                      ) : (
                        <span className="env-feedback-status read">Read</span>
                      )}
                    </td>
                    <td>
                      <div className="env-feedback-actions">
                        {!item.read && (
                          <button
                            className="btn btn-secondary btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(item.id);
                            }}
                            title="Mark as read"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-destructive btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFeedback(item.id);
                          }}
                          title="Delete feedback"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Feedback Detail Modal */}
      <Dialog
        open={selectedFeedback !== null}
        onClose={() => setSelectedFeedback(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        {selectedFeedback && (
          <>
            <DialogTitle
              sx={{
                m: 0,
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedFeedback.subject}
              <IconButton
                aria-label="close"
                onClick={() => setSelectedFeedback(null)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box className="env-feedback-detail-meta" sx={{mb: 3}}>
                <Box sx={{display: "flex", gap: 2, flexWrap: "wrap", mb: 2}}>
                  <Chip
                    label={
                      selectedFeedback.type === "anonymous"
                        ? "Anonymous"
                        : selectedFeedback.type === "bug"
                          ? "Bug Report"
                          : "General"
                    }
                    color={
                      selectedFeedback.type === "anonymous"
                        ? "secondary"
                        : selectedFeedback.type === "bug"
                          ? "error"
                          : "primary"
                    }
                    size="small"
                    icon={
                      selectedFeedback.type === "anonymous" ? (
                        <EyeOff size={16} />
                      ) : selectedFeedback.type === "bug" ? (
                        <Bug size={16} />
                      ) : (
                        <User size={16} />
                      )
                    }
                  />
                  {!selectedFeedback.read && (
                    <Chip label="New" color="warning" size="small" />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                  <strong>Submitted:</strong>{" "}
                  {formatDate(selectedFeedback.timestamp)}
                </Typography>

                {selectedFeedback.type === "general" &&
                  selectedFeedback.name && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{mb: 1}}
                    >
                      <strong>From:</strong> {selectedFeedback.name}
                      {selectedFeedback.email && ` (${selectedFeedback.email})`}
                    </Typography>
                  )}

                {/* Bug-specific fields */}
                {selectedFeedback.type === "bug" && (
                  <>
                    {selectedFeedback.siteLocation && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{mb: 1}}
                      >
                        <strong>Site Location:</strong>{" "}
                        {selectedFeedback.siteLocation}
                      </Typography>
                    )}
                    {selectedFeedback.expectedBehaviour && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{mb: 1}}
                      >
                        <strong>Expected Behaviour:</strong>
                      </Typography>
                    )}
                    {selectedFeedback.expectedBehaviour && (
                      <Box
                        sx={{
                          ml: 2,
                          mb: 1,
                          p: 1,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {selectedFeedback.expectedBehaviour}
                        </Typography>
                      </Box>
                    )}
                    {selectedFeedback.observedBehaviour && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{mb: 1}}
                      >
                        <strong>Observed Behaviour:</strong>
                      </Typography>
                    )}
                    {selectedFeedback.observedBehaviour && (
                      <Box
                        sx={{
                          ml: 2,
                          mb: 1,
                          p: 1,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {selectedFeedback.observedBehaviour}
                        </Typography>
                      </Box>
                    )}
                    {selectedFeedback.screenshotUrl && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{mb: 1}}
                      >
                        <strong>Screenshot:</strong>
                      </Typography>
                    )}
                    {selectedFeedback.screenshotUrl && (
                      <Box sx={{ml: 2, mb: 1}}>
                        <img
                          src={selectedFeedback.screenshotUrl}
                          alt="Bug screenshot"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}

                {selectedFeedback.userAgent && (
                  <Box sx={{mt: 2}}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{mb: 1}}
                    >
                      <strong>User Agent:</strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        p: 1,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                        display: "block",
                      }}
                    >
                      {selectedFeedback.userAgent}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box className="env-feedback-detail-message">
                <Typography variant="h6" sx={{mb: 2}}>
                  Message
                </Typography>
                <Box
                  className="env-feedback-message-content"
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  {selectedFeedback.message.split("\n").map((line, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{mb: line.trim() ? 1 : 0}}
                    >
                      {line || "\u00A0"}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{p: 2, gap: 1}}>
              {!selectedFeedback.read && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleMarkAsRead(selectedFeedback.id)}
                >
                  <CheckCircle size={16} />
                  Mark as Read
                </button>
              )}
              <button
                className="btn btn-destructive"
                onClick={() => handleDeleteFeedback(selectedFeedback.id)}
              >
                <Trash2 size={16} />
                Delete
              </button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        message="Are you sure you want to delete this feedback? This action cannot be undone."
      />
    </div>
  );
};

export default FeedbackManagement;
